import targetingApi from 'api/integration/targeting'
import i18n from 'i18n'
import { immutableSplice } from 'utils/helpers'
import { addLocation, locationContains } from './locationManager'

export const MIN_RADIUS_MILES = 10
export const MIN_RADIUS_KILOMETERS = 17
export const MAX_RADIUS_MILES = 50
export const MAX_RADIUS_KILOMETERS = 80

export const MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS = 10
export const MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS = 15
export const MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS_US_CA = 25
export const MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS_US_CA = 40

export const actions = Object.freeze({
  addLocation: 'addLocation',
  removeLocation: 'removeLocation',
  clearAll: 'clearAll',
  setLocationRadius: 'setLocationRadius',
  setQualifier: 'setQualifier',
  reset: 'reset',

  addLocationAsync: 'addLocationAsync',
  fetchSuggestedRadiusAsync: 'fetchgetSuggestedRadiusAsync',
  fetchSuggestedRadiusSuccess: 'fetchSuggestedRadiusSuccess',
  fetchSuggestedRadiusError: 'fetchSuggestedRadiusError',
  fetchSuggestedRadiusLoading: 'fetchSuggestedRadiusLoading',
})

export const _initialState = {
  locations: [],
}

const milesToKilometers = (miles) => {
  const km = miles * 1.60934

  return Math.max(MIN_RADIUS_KILOMETERS, Math.round(km))
}

const kilometersToMiles = (kilometers) => {
  const mi = kilometers / 1.60934

  return Math.max(MIN_RADIUS_MILES, Math.round(mi))
}

const dbLocationToLocation = (location, qualifier) => {
  const unit = getPreferredRangeUnit()
  return {
    data: location.location,
    qualifier,
    // supports both db and local formats
    radius: location.radius
      ? {
        value:
          unit === 'kilometers' ? location.radius.kilometer_range : location.radius.mile_range,
        unit,
      }
      : {
        value: unit === 'kilometers' ? location.kilometer_range : location.mile_range,
        unit,
      },
  }
}

export const initialState = (targeting = _initialState) => {
  const allowedLocationTypes = [
    'country',
    'country_group',
    'city',
    'place',
    'custom_location',
    'medium_geo_area',
    'geo_market',
    'region',
    'geo_market',
    'subcity',
    'county',
    'geo_market',
    'custom_location',
    'neighborhood',
    'electoral_district'
  ]
  let { rangeLocations: { included, excluded } = {} } = targeting
  included = included || []
  excluded = excluded || []
  const mappedIncludedLocations = included.map((x) => dbLocationToLocation(x, 'include'))
  const mappedExcludedLocations = excluded.map((x) => dbLocationToLocation(x, 'exclude'))
  const filteredIncludedLocations = mappedIncludedLocations.filter((location) =>
    allowedLocationTypes.includes(location.data.type),
  )
  const filteredExcludedLocations = mappedExcludedLocations.filter((location) =>
    allowedLocationTypes.includes(location.data.type),
  )
  return {
    ..._initialState,
    locations: [...filteredIncludedLocations, ...filteredExcludedLocations],
  }
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actions.addLocation: {
      // filter relevant locations by qualifier
      const { qualifier } = action.payload
      const { included, excluded } = state.locations.reduce((acc, location) => {
        if (location.qualifier === 'include') {
          acc.included.push(location)
        } else if (location.qualifier === 'exclude') {
          acc.excluded.push(location)
        }
        return acc
      }, { included: [], excluded: [] })
      
      // filter relevant locations by qualifier
      const relevantLocations = qualifier === 'include' ? included : excluded
      let otherLocations = qualifier === 'include' ? excluded : included
      const newLocations = addLocation(relevantLocations, action.payload)

      // Remove excluded supersets if adding an included subset
      if (qualifier === 'include') {
        otherLocations = otherLocations.filter(
          (loc) => !locationContains(loc.data, action.payload.data)
        )
      }

      return { ...state, locations: [...newLocations, ...otherLocations] }
    }
    case actions.removeLocation: {
      const { locationKey } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      const locations = immutableSplice(state.locations, idx, 1)
      return { ...state, locations }
    }
    case actions.clearAll:
      return { ...state, locations: [] }
    case actions.setLocationRadius: {
      const {
        locationKey,
        radius: { value, unit },
        minRadius,
      } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      const location = state.locations[idx]
      const locations = immutableSplice(state.locations, idx, 1, {
        ...location,
        radius: { value: clampRadius(value, unit, minRadius), unit },
        loading: false,
      })
      return { ...state, locations }
    }
    case actions.setQualifier: {
      const { locationKey, qualifier } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      const location = state.locations[idx]

      // readd location to make sure deduplication works correctly
      const { included, excluded } = state.locations.reduce((acc, location) => {
        if (location.qualifier === 'include') {
          acc.included.push(location)
        } else if (location.qualifier === 'exclude') {
          acc.excluded.push(location)
        }
        return acc
      }, { included: [], excluded: [] })
      
      // filter relevant locations by qualifier
      const relevantLocations = qualifier === 'include' ? included : excluded
      const newLocations = addLocation(relevantLocations, { ...location, qualifier })
      
      let otherLocations = qualifier === 'include' ? excluded : included
      // filter out the location that was just updated
      otherLocations = otherLocations.filter((loc) => loc.data.key !== locationKey)

      // Remove excluded supersets if changing to included
      if (qualifier === 'include') {
        otherLocations = otherLocations.filter(
          (loc) => !locationContains(loc.data, location.data)
        )
      }

      return { ...state, locations: [...newLocations, ...otherLocations] }
    }
    case actions.reset: {
      const { tcTargeting, locations } = action.payload
      const newState = initialState(tcTargeting)
      if (locations) {
        newState.locations = locations
      }
      return newState
    }
    case actions.fetchSuggestedRadiusError: {
      const { locationKey: errorLocationKey, error } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === errorLocationKey)
      const location = state.locations[idx]

      const locations = immutableSplice(state.locations, idx, 1, {
        ...location,
        loading: false,
        error,
      })

      return { ...state, locations }
    }
    case actions.fetchSuggestedRadiusLoading: {
      const { locationKey: loadinglocationKey } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === loadinglocationKey)
      const location = state.locations[idx]
      const locations = immutableSplice(state.locations, idx, 1, { ...location, loading: true })
      return { ...state, locations }
    }
    default:
      return state
  }
}

export const asyncActionHandlers = {
  [actions.addLocationAsync]:
    ({ dispatch }) =>
      async (action) => {
        const { location: data, qualifier } = action.payload
        const unit = getPreferredRangeUnit()
        const payload = {
          data,
          qualifier,
          loading: data.type === 'city' || data.type === 'place', // fetch suggested radius for cities and addresses
          radius: (data.type === 'city' || data.type === 'place') ? { value: getDefaultRadiusValue(unit), unit } : null,
        }

        dispatch({ type: actions.addLocation, payload })

        if (data.type === 'city' || data.type === 'place') {
          const loc = `${data.country_name}, ${data.name}`
          const response = await targetingApi.getSuggestedRadiusAsync(loc, unit)
          if (response.status !== 200) {
            dispatch({
              type: actions.fetchSuggestedRadiusError,
              payload: { locationKey: data.key, error: 'Error fetching radius' },
            })
            return
          }

          const radius = Math.max(
            getDefaultRadiusValue(unit),
            response.data.suggest_radius || getDefaultRadiusValue(unit),
          )
          dispatch({
            type: actions.setLocationRadius,
            payload: { locationKey: data.key, radius: { value: radius, unit } },
          })
        }
      },
}

export function getPreferredRangeUnit() {
  return i18n.language === 'en' ? 'miles' : 'kilometers'
}

export function clampRadius(value, unit, minRadius) {
  if (unit === 'miles') {
    return Math.min(Math.max(value, minRadius ?? MIN_RADIUS_MILES), MAX_RADIUS_MILES)
  } else {
    return Math.min(Math.max(value, minRadius ?? MIN_RADIUS_KILOMETERS), MAX_RADIUS_KILOMETERS)
  }
}

export function getDefaultRadiusValue(unit) {
  return unit === 'miles' ? MIN_RADIUS_MILES : MIN_RADIUS_KILOMETERS
}

export function getRadiusVariations(radius) {
  if (!radius) {
    return {}
  }

  const { value, unit } = radius
  return {
    mile_range: unit === 'miles' ? value : kilometersToMiles(value),
    kilometer_range: unit === 'kilometers' ? value : milesToKilometers(value),
  }
}

function isRemoteTargetingRangeLocationsValid(tcTargeting) {
  let { rangeLocations: { included, excluded } = {} } = tcTargeting
  included = included || []
  excluded = excluded || []

  return included.length > 0 || excluded.length > 0
}

function isRangeLocationTargetingValid(locations) {
  return locations.length > 0
}

export function shouldOverrideLocalState(tcTargeting, locations) {
  return (
    isRemoteTargetingRangeLocationsValid(tcTargeting) && !isRangeLocationTargetingValid(locations)
  )
}
